import { Box, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import { CampaignChannels } from 'domains/campaigns/types';
import { isTemplate, ContentTypeForComparison, ContentTypeEnumForBackend } from 'domains/content/types';
import PageLoader from 'domains/core/components/PageLoader';
import useUpdateContentStatus from 'hooks/mutations/useUpdateContentStatus';
import QueryKeys from 'hooks/queries/keys';
import useDirectMail from 'hooks/queries/useDirectMail';
import useDirectMailSenderProfile from 'hooks/queries/useDirectMailSenderProfile';
import useSenderProfile from 'hooks/queries/useSenderProfile';
import useTemplate from 'hooks/queries/useTemplate';
import useVoiceContentById from 'hooks/queries/useVoiceContentById';
import useWhatsApp from 'hooks/queries/useWhatsApp';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useQueryParams from 'hooks/useQueryParams';
import { URLPaths } from 'models/enums';
import useSmsData from 'pages/ContentSmsBuilderPage/hooks/useSmsData';

import ContentStatusText, { ContentStatus } from '../ContentTable/ContentStatusText';
import { getContentCreatePath, contentTypeToSnakeCase } from '../ContentTable/utils';

import ContentPreviewScreen from './ContentPreviewScreen';
import ContentViewScreenHeader from './ContentViewScreenHeader';

type Props = {
    channel: CampaignChannels;
};

const ContentViewScreen = ({ channel }: Props) => {
    const { canManageContent } = useAccessCentrePermissions();
    /** `content.manage` shows Edit; `content.view` alone does not (preview-only). */
    const showEditButton = canManageContent;
    const query = useQueryParams();
    const history = useHistory();
    const queryClient = useQueryClient();

    // Get parameters from query
    const contentId = query.get('id');
    const senderProfileId = query.get('senderProfileId');
    const parentFolderId = query.get('parentFolderId');
    const status = query.get('status') as ContentStatus;

    // Fetch all data in one place - used by both ContentViewScreen and ContentPreviewScreen
    const { data: previewTemplate, isInitialLoading: isTemplateLoading } = useTemplate({
        templateId: contentId || '',
        reactQueryOptions: {
            enabled: channel === CampaignChannels.EMAIL && !!contentId,
        },
    });

    const { data: emailSenderProfile } = useSenderProfile(senderProfileId ? parseInt(senderProfileId) : undefined, {
        enabled: channel === CampaignChannels.EMAIL && !!senderProfileId,
    });

    const { smsData, mergeTags, isLoading: isSmsLoading } = useSmsData({
        id: contentId || '',
        enabled: channel === CampaignChannels.SMS && !!contentId,
    });

    const { data: whatsAppData, isInitialLoading: isWhatsAppLoading } = useWhatsApp(contentId, {
        enabled: channel === CampaignChannels.WHATSAPP && !!contentId,
    });

    const { data: voiceData, isInitialLoading: isVoiceLoading } = useVoiceContentById({
        voiceContentId: contentId || '',
        reactQueryOptions: {
            enabled: channel === CampaignChannels.VOICE_CALL && !!contentId,
        },
    });

    const { data: previewDirectMail, isInitialLoading: isDirectMailLoading } = useDirectMail(
        contentId ? parseInt(contentId) : undefined,
        { enabled: channel === CampaignChannels.DIRECT_MAIL && !!contentId }
    );

    const { data: directMailSenderProfile } = useDirectMailSenderProfile({
        senderProfileId: senderProfileId ? parseInt(senderProfileId) : undefined,
        reactQueryOptions: { enabled: channel === CampaignChannels.DIRECT_MAIL && !!senderProfileId },
    });

    const isContentLoading =
        isTemplateLoading || isSmsLoading || isWhatsAppLoading || isVoiceLoading || isDirectMailLoading;

    // Get content name based on channel
    const getContentName = () => {
        switch (channel) {
            case CampaignChannels.EMAIL:
                return isTemplate(previewTemplate) ? previewTemplate.name : '';
            case CampaignChannels.SMS:
                return smsData?.name || '';
            case CampaignChannels.VOICE_CALL:
                return voiceData?.name || '';
            case CampaignChannels.DIRECT_MAIL:
                return previewDirectMail?.name || '';
            case CampaignChannels.WHATSAPP:
                return whatsAppData?.name || '';
            default:
                return '';
        }
    };

    const contentName = getContentName();
    // Map CampaignChannels to ContentTypeForComparison
    const getContentTypeFromChannel = (channel: CampaignChannels): ContentTypeForComparison => {
        switch (channel) {
            case CampaignChannels.EMAIL:
                return ContentTypeForComparison.EMAIL;
            case CampaignChannels.SMS:
                return ContentTypeForComparison.SMS;
            case CampaignChannels.VOICE_CALL:
                return ContentTypeForComparison.VOICE;
            case CampaignChannels.DIRECT_MAIL:
                return ContentTypeForComparison.DIRECT_MAIL;
            case CampaignChannels.WHATSAPP:
                return ContentTypeForComparison.WHATSAPP;
        }
    };
    const contentType = getContentTypeFromChannel(channel);
    const isPublished = status === ContentStatus.PUBLISHED;

    const { mutate: updateContentStatus } = useUpdateContentStatus(contentId || '', {
        onSuccess: () => {
            if (contentId) {
                // Invalidate cache to ensure fresh data is fetched on the edit page
                queryClient.invalidateQueries([QueryKeys.TEMPLATE_ID, contentId]);
                queryClient.invalidateQueries([QueryKeys.SMS_ID, contentId]);
                queryClient.invalidateQueries([QueryKeys.WHATSAPP_TEMPLATE_ID, contentId]);
                queryClient.invalidateQueries([QueryKeys.VOICE_CONTENT_ID, contentId]);
                queryClient.invalidateQueries([QueryKeys.DIRECT_MAIL_ID, contentId]);

                const path = getContentCreatePath({
                    contentType,
                    id: parseInt(contentId),
                    parentFolderId: parentFolderId ? parseInt(parentFolderId) : undefined,
                    status: ContentStatus.DRAFT,
                });
                history.push(path);
            }
        },
    });

    // Handle Edit - navigate to create path
    const handleEdit = () => {
        if (contentId) {
            if (isPublished) {
                // Convert published to draft before navigating
                const channelForBackend = contentTypeToSnakeCase(contentType as keyof typeof ContentTypeEnumForBackend);
                updateContentStatus({
                    status: ContentStatus.DRAFT,
                    channel: channelForBackend,
                });
            } else {
                // Navigate to builder page - status will be fetched from the content API
                const path = getContentCreatePath({
                    contentType,
                    id: parseInt(contentId),
                    parentFolderId: parentFolderId ? parseInt(parentFolderId) : undefined,
                    status,
                });
                history.push(path);
            }
        }
    };

    // Handle Exit - go back
    const handleExit = () => {
        history.push(URLPaths.CONTENT);
    };

    if (isContentLoading) {
        return <PageLoader />;
    }

    return (
        <Stack rowGap={4} height="100%" sx={{ overflow: 'hidden' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2, pt: 3, flexShrink: 0 }}>
                <Typography>{contentName || 'Content Name'}</Typography>
                {status && (
                    <Box>
                        <ContentStatusText status={status} />
                    </Box>
                )}
            </Box>
            <Stack flex={1} sx={{ minHeight: 0, overflow: 'hidden' }}>
                <ContentViewScreenHeader
                    channel={channel}
                    onEdit={handleEdit}
                    onExit={handleExit}
                    showEditButton={showEditButton}
                    isEditDisabled={status === ContentStatus.LIVE}
                />
                <ContentPreviewScreen
                    channel={channel}
                    previewTemplate={previewTemplate}
                    emailSenderProfile={emailSenderProfile}
                    smsData={smsData}
                    mergeTags={mergeTags}
                    voiceData={voiceData}
                    previewDirectMail={previewDirectMail}
                    directMailSenderProfile={directMailSenderProfile}
                    whatsAppData={whatsAppData}
                />
            </Stack>
        </Stack>
    );
};

export default ContentViewScreen;
